/**
 * Quantity pricing (S3.1) — pure, zero I/O; the caller supplies dataset rows.
 *
 * Citations (ux-lens: ship only the cited subset):
 * - Schedule entity: DD!116 Quantity Pricing Schedule 數量定價排程 (J=COULD HAVE).
 * - Aggregation basis: DD!117 Calculate Quantity Discounts 數量折扣計算方式;
 *   the only DRAWN value is "By Line Quantity" (Pricing Engine board, NetSuite
 *   Pricing-section screenshot) — so `by_line_quantity` is the only shipped
 *   basis and the bracket lookup runs on the caller's line qty.
 * - Discount math: BLANKET bracket discount off the unit price — the
 *   −Discount 量價折扣 step of the Pricing Engine 流程 waterfall.
 *
 * TODO(citation-needed): NetSuite "Use Marginal Rates" (marginal per-bracket
 * slices) and the overall_item / overall_parent / overall_schedule aggregation
 * bases exist only in the research digest
 * (research--netsuite-item-pricing-inventory-semantics.md). No board asset or
 * xlsx DD row cites them — extend here (plus the divergence tests named in
 * plan §S3.1 acceptance) only when a citation lands.
 *
 * Either-or invariant (plan §S3.1): a product references a schedule XOR
 * carries ad-hoc brackets. Both set = invalid dataset → this module THROWS
 * (fail fast; seeds are validated by their own suites).
 */

import type {
  ProductPricingFields,
  QuantityBracket,
  QuantityPricingSchedule,
} from '@/types/api/pricing';

// ---------------------------------------------------------------------------
// Numeric helper (hosted here — the S3.1 module-graph root — so every pricing
// module, resolvePrice included, shares it without an import cycle; it is
// re-exported from ./resolvePrice and the @/lib/pricing barrel unchanged).
// ---------------------------------------------------------------------------

/**
 * ROUND_HALF_UP to 2 decimal places, float-safe.
 * Uses the exponent-shift trick so 2.675 → 2.68 (a plain
 * `Math.round(x * 100) / 100` yields 2.67 because 2.675 * 100 = 267.49999…).
 */
export function roundHalfUp2(value: number): number {
  const shifted = Number(`${value}e2`);
  if (!Number.isFinite(shifted)) return NaN;
  return Number(`${Math.round(shifted)}e-2`);
}

// ---------------------------------------------------------------------------
// Schedule / bracket resolution
// ---------------------------------------------------------------------------

/** Find a schedule by id; null when absent (dangling ids skip gracefully). */
export function findQuantitySchedule(
  schedules: QuantityPricingSchedule[] | undefined,
  scheduleId: string
): QuantityPricingSchedule | null {
  return schedules?.find((schedule) => schedule.id === scheduleId) ?? null;
}

/**
 * Apply period 適用期間 (2026-08-28; SAP condition-record Valid-From/To
 * precedent): ISO dates, inclusive on both ends; an absent side is open.
 * No `asOf` = no gating (callers that don't date-resolve keep today's
 * behavior). String compare is correct for ISO YYYY-MM-DD.
 */
export function scheduleAppliesOn(
  schedule: Pick<QuantityPricingSchedule, 'appliesFrom' | 'appliesTo'>,
  asOf: string | undefined
): boolean {
  if (!asOf) return true;
  if (schedule.appliesFrom && asOf < schedule.appliesFrom) return false;
  if (schedule.appliesTo && asOf > schedule.appliesTo) return false;
  return true;
}

/**
 * Effective brackets for a product: from its referenced schedule (active
 * only) XOR its ad-hoc brackets. THROWS on the either-or violation; returns
 * null when the product has no quantity pricing (an empty ad-hoc array counts
 * as "not set").
 */
export function getEffectiveQuantityBrackets(
  fields: ProductPricingFields | undefined,
  schedules: QuantityPricingSchedule[] | undefined,
  /** Resolution date — gates the schedule's 適用期間; absent = no gating. */
  asOf?: string
): QuantityBracket[] | null {
  if (!fields) return null;
  const scheduleId = fields.quantityScheduleId ?? null;
  const adHoc = fields.quantityBrackets ?? null;
  const hasAdHoc = adHoc !== null && adHoc.length > 0;
  if (scheduleId !== null && scheduleId !== '' && hasAdHoc) {
    throw new Error(
      `quantity pricing for product ${fields.productId} sets BOTH quantityScheduleId ` +
        'and ad-hoc quantityBrackets — the assignment is either-or (plan §S3.1)'
    );
  }
  if (scheduleId !== null && scheduleId !== '') {
    const schedule = findQuantitySchedule(schedules, scheduleId);
    if (!schedule || !schedule.isActive || schedule.brackets.length === 0) return null;
    // Ad-hoc brackets carry no period — only referenced schedules are dated.
    if (!scheduleAppliesOn(schedule, asOf)) return null;
    return schedule.brackets;
  }
  return hasAdHoc ? adHoc : null;
}

/**
 * True when the product references a schedule that exists, is active and has
 * brackets, but `asOf` falls OUTSIDE its 適用期間 — i.e. the only reason it
 * contributed nothing is the period. Lets the resolver trace name
 * `outside_apply_period` instead of laundering it as `no_rule_found`.
 */
export function isOutsideSchedulePeriod(
  fields: ProductPricingFields | undefined,
  schedules: QuantityPricingSchedule[] | undefined,
  asOf: string | undefined
): boolean {
  const scheduleId = fields?.quantityScheduleId ?? null;
  if (scheduleId === null || scheduleId === '') return false;
  const schedule = findQuantitySchedule(schedules, scheduleId);
  if (!schedule || !schedule.isActive || schedule.brackets.length === 0) return false;
  return !scheduleAppliesOn(schedule, asOf);
}

/**
 * Bracket lookup by LINE quantity (`by_line_quantity`, the only cited basis):
 * the highest `minQuantity` that is ≤ qty wins; qty below every bracket → null.
 */
export function selectQuantityBracket(
  brackets: QuantityBracket[],
  qty: number
): QuantityBracket | null {
  let selected: QuantityBracket | null = null;
  for (const bracket of [...brackets].sort((a, b) => a.minQuantity - b.minQuantity)) {
    if (qty >= bracket.minQuantity) selected = bracket;
  }
  return selected;
}

/**
 * Blanket quantity discount: unitPrice × (1 − discountPct/100), ROUND_HALF_UP
 * 2dp (every derived step rounds — plan §S3.1).
 */
export function applyQuantityDiscount(unitPrice: number, discountPct: number): number {
  return roundHalfUp2(unitPrice * (1 - discountPct / 100));
}
